import React, { useRef, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Project } from '../../types/portal';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';
export function ProjectFields({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    activeProject,
    updateProject,
    notify
  } = usePortal();
  const [draft, setDraft] = useState<Project>(activeProject);
  const fileInput = useRef<HTMLInputElement>(null);
  function edit(patch: Partial<Project>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function uploadCallSheet(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    edit({
      callSheet: {
        fileName: file.name,
        url: URL.createObjectURL(file)
      }
    });
    event.target.value = '';
  }
  function save(event: React.FormEvent) {
    event.preventDefault();
    updateProject({
      client: draft.client.trim() || activeProject.client,
      name: draft.name.trim() || activeProject.name,
      type: draft.type,
      description: draft.description,
      nextShootDate: draft.nextShootDate,
      nextShootTime: draft.nextShootTime,
      callSheet: draft.callSheet,
      location: draft.location,
      clientContact: draft.clientContact
      /* Portal responsibilities live in the Portal Team section (project_staff),
         so there is deliberately no producer field to type twice. */
    });
    notify('Project details updated');
    onClose();
  }
  return <form onSubmit={save} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-286" data-mp="2ypg389ep304">
      <h3 className="font-display text-2xl leading-none" data-id="element-287" data-mp="3ub0k1sbjzs7m">Edit Project</h3>

      <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-288" data-mp="3pdh4mp34d9e3">
        <label className="block" data-id="element-289" data-mp="3pb4p0iku3b47">
          <span className="label block text-paper/40" data-id="element-290" data-mp="qngw719q1uwj">Client Name</span>
          <input value={draft.client} onChange={event => edit({
          client: event.target.value
        })} className={FIELD} data-id="element-291" data-mp="lw8pwe3oupnl" />
        </label>
        <label className="block" data-id="element-292" data-mp="2g675km0lkmxm">
          <span className="label block text-paper/40" data-id="element-293" data-mp="2k5fxrmfzocr2">Project Name</span>
          <input value={draft.name} onChange={event => edit({
          name: event.target.value
        })} className={FIELD} data-id="element-294" data-mp="2k7im821htxto" />
        </label>
        <label className="block" data-id="element-295" data-mp="2bbt72aqn1zoq">
          <span className="label block text-paper/40" data-id="element-296" data-mp="1wg3fzy6liwd5">Project Type</span>
          <input value={draft.type} onChange={event => edit({
          type: event.target.value
        })} placeholder="e.g. Event Coverage" className={FIELD} data-id="element-297" data-mp="1wesde1ovzunm" />
        </label>
        <div className="block" data-id="element-298" data-mp="1nhrta78uoipz">
          <span className="label block text-paper/40" data-id="element-299" data-mp="1sdqanavrh0gw">Project Status</span>
          <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-300" data-mp="3htg8maizunaw">
            {projectStatus(activeProject)}
          </p>
          <span className="label-sm mt-3 block text-paper/30" data-id="element-301" data-mp="3ms12ls7nkaqw">
            Set automatically from the deliverables
          </span>
        </div>
        <label className="block" data-id="element-302" data-mp="3rxmz9ij961a2">
          <span className="label block text-paper/40" data-id="element-303" data-mp="3ryo9jksg2jqq">Next Shoot Date</span>
          <input type="date" value={draft.nextShootDate} onChange={event => edit({
          nextShootDate: event.target.value
        })} className={FIELD} data-id="element-304" data-mp="9jx4w44lngr7" />
        </label>
        <label className="block" data-id="element-305" data-mp="jgiez3vh7i1w">
          <span className="label block text-paper/40" data-id="element-306" data-mp="jjo3agwa6fwk">Next Shoot Time (optional)</span>
          <input value={draft.nextShootTime} onChange={event => edit({
          nextShootTime: event.target.value
        })} placeholder="e.g. 10:00 AM" className={FIELD} data-id="element-307" data-mp="ehytt5opiaal" />
        </label>
        <label className="block" data-id="element-308" data-mp="2u9mmgowotgnl">
          <span className="label block text-paper/40" data-id="element-309" data-mp="2u8us1hettoyw">Shoot Location (optional)</span>
          <input value={draft.location} onChange={event => edit({
          location: event.target.value
        })} placeholder="e.g. Portal Studio A · Al Quoz" className={FIELD} data-id="element-310" data-mp="2q8j51xyzrck9" />
        </label>
        <div className="block" data-id="element-311" data-mp="2wmq57p42vnr6">
          <span className="label block text-paper/40" data-id="element-312" data-mp="2wq44uplcxiux">Call Sheet (optional)</span>
          {draft.callSheet ? <>
              <p className="mt-3 truncate border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-313" data-mp="35p7qo1bwscd4">
                {draft.callSheet.fileName}
              </p>
              <div className="mt-3 flex items-center gap-6" data-id="element-314" data-mp="2d00tb5o6xstg">
                <button type="button" onClick={() => fileInput.current?.click()} className="label text-paper/50 transition-colors duration-150 ease-out hover:text-paper" data-id="element-315" data-mp="2d0jzjp4hasrn">
                  Replace
                </button>
                <button type="button" onClick={() => edit({
              callSheet: undefined
            })} className="label text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-316" data-mp="1pa6folw1s085">
                  Remove
                </button>
              </div>
            </> : <button type="button" onClick={() => fileInput.current?.click()} className="label mt-3 w-full border border-paper/25 px-4 py-3 text-left text-paper/50 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-317" data-mp="1cru3zfyy49et">
              Upload PDF
            </button>}
          <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={uploadCallSheet} className="hidden" data-id="element-318" data-mp="yz4ciw0necab" />
        </div>
        <label className="block" data-id="element-319" data-mp="p4ch9yujg1ip">
          <span className="label block text-paper/40" data-id="element-320" data-mp="l0oahidqd9yi">Main Client Contact</span>
          <input value={draft.clientContact} onChange={event => edit({
          clientContact: event.target.value
        })} placeholder="e.g. Sarah Jones · Marketing Director" className={FIELD} data-id="element-321" data-mp="l2qyjwvbot0l" />
        </label>
      </div>

      <label className="mt-8 block" data-id="element-322" data-mp="ahdtqeysxr99">
        <span className="label block text-paper/40" data-id="element-323" data-mp="3slfe13hya2er">Project Description</span>
        <textarea value={draft.description} onChange={event => edit({
        description: event.target.value
      })} rows={3} placeholder="e.g. A three-part leadership series filmed at the client's Dubai office." className={`${FIELD} resize-none`} data-id="element-324" data-mp="3snjrod5nifs8" />
      </label>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-325" data-mp="u5mief14zf40">
        <button type="submit" className={BUTTON_SOLID} data-id="element-326" data-mp="fedzeorqorzu">
          Save Project
        </button>
        <button type="button" onClick={onClose} className={BUTTON_QUIET} data-id="element-327" data-mp="kcp7atyh4sff">
          Cancel
        </button>
      </div>
    </form>;
}